/**
 * Presentational helpers shared across the Guardrails components, plus the
 * client-side file download used by "Download", "Download draft" and
 * "Download template". Pure except for `downloadTextFile`, which touches the DOM.
 */
import type { AssistantPolicy, User } from '../../lib/types.js';

/** The feature's base path — every guardrails URL hangs off this. */
export const GUARDRAILS_PATH = '/settings/guardrails';

/**
 * A formatted-but-unsaved upload. Transient by design — it only exists between
 * the formatter returning and the admin saving or discarding, so /review
 * receives it via navigation state and redirects to the list when it's absent
 * (reload, direct link).
 */
export interface UploadDraft {
  filename: string;
  /** Bytes of the original upload, for the review screen's meta line. */
  size: number;
  document: string;
  rulesCount: number;
  leftOut: string[];
  /** Stored alongside the document so the original wording stays recoverable. */
  sourceText: string;
}

/** What the list page hands /review through navigation state. */
export interface ReviewLocationState {
  draft: UploadDraft;
}

/** Trigger a browser download of in-memory text. No server round-trip. */
export const downloadTextFile = (
  filename: string,
  contents: string,
  mimeType = 'text/markdown',
): void => {
  const url = URL.createObjectURL(new Blob([contents], { type: mimeType }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/** A safe .md filename for a document name ("Field safety" → "field-safety.md"). */
export const documentFileName = (name: string): string => {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${slug || 'guardrails'}.md`;
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });

/**
 * "Updated {date} by {user}". `updatedByUserId` may be null (a document written
 * before the column existed, or by a non-user actor) and the users list may not
 * contain a since-removed user — in both cases the "by" clause is dropped rather
 * than printing a raw id.
 */
export const updatedMeta = (policy: AssistantPolicy, users: User[]): string => {
  const base = `Updated ${formatDate(policy.updatedAt)}`;
  if (!policy.updatedByUserId) return base;
  const author = users.find((u) => u.id === policy.updatedByUserId);
  if (!author) return base;
  return `${base} by ${author.name ?? author.email}`;
};
